/**
 * Browser compatibility detection and fallbacks for iOS Safari
 */

export interface BrowserCapabilities {
  isIOS: boolean;
  isOldIOS: boolean;
  safariVersion: number | null;
  supportsDragEvents: boolean;
  supportsPassiveEvents: boolean;
  supportsTouchEvents: boolean;
  recommendTapMode: boolean;
}

/**
 * Detect iOS Safari version and capabilities
 */
export function detectBrowserCapabilities(): BrowserCapabilities {
  const userAgent = navigator.userAgent;
  
  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  
  // Detect Safari version on iOS
  let safariVersion: number | null = null;
  let isOldIOS = false;
  
  if (isIOS) {
    // Extract iOS version from user agent
    const iosVersionMatch = userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
    if (iosVersionMatch) {
      const majorVersion = parseInt(iosVersionMatch[1]);
      
      // iOS versions map roughly to Safari versions
      // iOS 12 = Safari 12, iOS 13 = Safari 13, etc.
      safariVersion = majorVersion;
      isOldIOS = majorVersion < 13;
    }
  }
  
  // Test passive event support
  let supportsPassiveEvents = false;
  try {
    const options = {
      get passive() {
        supportsPassiveEvents = true;
        return false;
      }
    };
    const testFn = () => {};
    window.addEventListener('test' as any, testFn, options as any);
    window.removeEventListener('test' as any, testFn, options as any);
  } catch (e) {
    supportsPassiveEvents = false;
  }
  
  // Touch events are well supported on iOS
  const supportsTouchEvents = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Drag events have issues on older iOS Safari
  const supportsDragEvents = !isOldIOS && supportsTouchEvents;
  
  // Recommend tap mode for older iOS or if drag events are unreliable
  const recommendTapMode = isOldIOS || !supportsDragEvents;
  
  return {
    isIOS,
    isOldIOS,
    safariVersion,
    supportsDragEvents,
    supportsPassiveEvents,
    supportsTouchEvents,
    recommendTapMode,
  };
}

/**
 * Get touch event options with passive support fallback
 */
export function getTouchEventOptions(passive: boolean = true): boolean | { passive: boolean } {
  const capabilities = detectBrowserCapabilities();
  
  if (capabilities.supportsPassiveEvents) {
    return { passive };
  }
  
  // Fallback for browsers that don't support passive events
  return false;
}

/**
 * Safe preventDefault that works on older iOS
 */
export function safePreventDefault(event: Event): void {
  try {
    // Only prevent default if the event is cancelable
    if (event.cancelable) {
      event.preventDefault();
    }
  } catch (e) {
    // Ignore errors on older browsers
    console.warn('preventDefault failed:', e);
  }
}

/**
 * Show compatibility warning for older iOS users
 */
export function shouldShowCompatibilityWarning(): boolean {
  const capabilities = detectBrowserCapabilities();
  
  // Show warning for iOS < 12 or if drag events aren't supported
  return capabilities.isOldIOS && capabilities.safariVersion !== null && capabilities.safariVersion < 12;
}

/**
 * Get recommended settings for current browser
 */
export function getRecommendedSettings(): { dragMode: boolean; confirmMode: boolean } {
  const capabilities = detectBrowserCapabilities();
  
  return {
    // Recommend tap mode for older iOS or unreliable drag support
    dragMode: !capabilities.recommendTapMode,
    // Enable confirm mode on older iOS to prevent accidental taps
    confirmMode: capabilities.isOldIOS || capabilities.recommendTapMode,
  };
}